import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema, MoneySchema } from '../common/index.js';

/**
 * Catalogue d'événements v1 (architecture §3.4). Chaque écriture métier insère, dans la même transaction,
 * la ligne métier, une entrée d'audit et un événement (outbox) ; le worker publie au moins une fois.
 * Les charges ne transportent que des identifiants et des états, jamais un contenu sensible en clair.
 */

const dossierId = IdSchema;

interface DefinitionEvenement<T extends string, S extends z.ZodRawShape> {
  type: T;
  version: 1;
  producteur: string;
  consommateurs: readonly string[];
  description: string;
  charge: z.ZodObject<S>;
}

const ev = <T extends string, S extends z.ZodRawShape>(
  type: T,
  producteur: string,
  consommateurs: readonly string[],
  description: string,
  shape: S,
): DefinitionEvenement<T, S> => ({ type, version: 1, producteur, consommateurs, description, charge: z.object(shape) });

const ETAPE = { dossierId, stepId: IdSchema, phase: z.string().min(2) };
const PERSONNE = { dossierId, personId: IdSchema };
const DOCUMENT = { dossierId, documentId: IdSchema, personId: IdSchema.nullable() };
const MESSAGE = { dossierId, personId: IdSchema, messageId: IdSchema, channel: z.enum(['whatsapp', 'email', 'sms', 'postal']) };
const RELANCE = { dossierId, followupPlanId: IdSchema, personId: IdSchema.nullable() };
const IMPERSONATION = { impersonationSessionId: IdSchema, actorUserId: IdSchema, effectiveUserId: IdSchema };

export const EVENEMENTS = [
  ev('dossier.created', 'L31', ['tableau de bord', 'échéances'], 'Dossier créé.', { dossierId, reference: z.string() }),
  ev('dossier.phase_changed', 'L31', ['tableau de bord', 'échéances', 'portail'], 'Phase courante changée.', {
    dossierId,
    from: z.string(),
    to: z.string(),
  }),
  ev('dossier.blocked', 'L31', ['tableau de bord', 'décisions'], 'Blocage posé sur le dossier.', { dossierId, blockerKind: z.string() }),
  ev('dossier.closed', 'L31', ['rétention', 'liens portail'], 'Dossier clos ; accès externe limité ensuite.', {
    dossierId,
    closedAt: InstantSchema,
    externalAccessUntil: InstantSchema,
  }),
  ev('step.added', 'L31', ['journal'], 'Étape ajoutée à la procédure du dossier.', ETAPE),
  ev('step.skipped', 'L31', ['journal'], 'Étape sautée, avec motif journalisé.', ETAPE),
  ev('step.reordered', 'L31', ['journal'], 'Étapes réordonnées.', { dossierId, stepIds: z.array(IdSchema) }),
  ev('mandate.signed', 'L36', ['garde de recherche', 'facturation', 'relances'], 'Mandat, devis ou procuration signé.', {
    dossierId,
    mandateId: IdSchema,
    kind: z.enum(['mandat_recherche', 'devis', 'procuration', 'pouvoir']),
    signedAt: InstantSchema,
  }),
  ev('person.added', 'L32', ['dévolution', 'relances'], 'Personne ajoutée à l’arbre.', PERSONNE),
  ev('person.updated', 'L32', ['dévolution', 'estimation'], 'État civil ou statut d’une personne modifié.', {
    ...PERSONNE,
    fields: z.array(z.string()),
  }),
  ev('relationship.changed', 'L32', ['dévolution'], 'Lien de filiation ou d’union ajouté, modifié ou retiré.', {
    dossierId,
    relationshipId: IdSchema,
  }),
  ev('heir_option.changed', 'L32', ['dévolution', 'relances'], 'Option successorale d’un héritier modifiée.', {
    ...PERSONNE,
    successionId: IdSchema,
    option: z.enum(['silence', 'acceptation', 'acceptation_actif_net', 'renonciation']),
  }),
  ev('devolution.recomputed', 'L32', ['estimation', 'calendrier de collecte', 'portail'], 'Dévolution recalculée.', {
    dossierId,
    successionId: IdSchema,
    devolutionRunId: IdSchema,
    inputHash: z.string(),
  }),
  ev('document.received', 'L36', ['checklist', 'relances (arrêt)', 'tâches'], 'Pièce reçue.', DOCUMENT),
  ev('document.validated', 'L36', ['checklist'], 'Pièce validée.', DOCUMENT),
  ev('document.rejected', 'L36', ['checklist', 'relances'], 'Pièce refusée ou meilleure photo demandée.', {
    ...DOCUMENT,
    reason: z.enum(['illisible', 'mauvaise_piece', 'perimee', 'autre']),
  }),
  ev('message.received', 'L34', ['relances (arrêt immédiat)', 'rattachement', 'transcription'], 'Message entrant reçu.', {
    dossierId: IdSchema.nullable(),
    personId: IdSchema.nullable(),
    threadId: IdSchema,
    messageId: IdSchema,
    channel: z.enum(['whatsapp', 'email', 'sms', 'postal']),
    hasMedia: z.boolean(),
  }),
  ev('message.sent', 'L34', ['relances', 'journal'], 'Message parti.', MESSAGE),
  ev('message.delivered', 'L34', ['relances', 'journal'], 'Message délivré.', MESSAGE),
  ev('message.read', 'L34', ['relances (lu sans réponse)', 'journal'], 'Message lu.', MESSAGE),
  ev('message.failed', 'L34', ['relances', 'tâches'], 'Échec d’envoi.', { ...MESSAGE, attempts: z.number().int().min(1) }),
  ev('call.logged', 'L31/L34', ['relances', 'e-mail E2'], 'Appel journalisé.', {
    ...PERSONNE,
    callId: IdSchema,
    outcome: z.enum(['joint', 'messagerie', 'pas_de_reponse', 'mauvais_numero']),
  }),
  ev('followup.scheduled', 'L35', ['file du jour'], 'Relance programmée.', { ...RELANCE, at: InstantSchema }),
  ev('followup.cancelled', 'L35', ['file du jour'], 'Relance annulée (réponse, pause, plafond).', {
    ...RELANCE,
    reason: z.enum(['reponse', 'pause', 'plafond', 'consentement', 'manuel']),
  }),
  ev('followup.paused', 'L35', ['file du jour'], 'Relances en pause « on laisse respirer ».', {
    ...RELANCE,
    until: LocalDateSchema.nullable(),
  }),
  ev('followup.resumed', 'L35', ['file du jour'], 'Relances reprises.', RELANCE),
  ev('followup.cap_reached', 'L35', ['file du jour'], 'Plafond hebdomadaire atteint.', { ...RELANCE, weekStart: LocalDateSchema }),
  ev('consent.changed', 'L38', ['relances (blocage si retrait)', 'masquage'], 'Consentement modifié.', {
    ...PERSONNE,
    purpose: z.string(),
    granted: z.boolean(),
  }),
  ev('sensitive.shared', 'L38', ['journal', 'alerte'], 'Étiquette sensible partagée hors équipe.', {
    ...PERSONNE,
    sensitiveLabelId: IdSchema,
    recipientCount: z.number().int().min(1),
  }),
  ev('payment.succeeded', 'L37', ['relances P1', 'portail', 'tableau de bord'], 'Paiement réussi.', {
    ...PERSONNE,
    paymentId: IdSchema,
    amount: MoneySchema,
  }),
  ev('payment.failed', 'L37', ['relances P1', 'tableau de bord'], 'Paiement échoué.', { ...PERSONNE, paymentId: IdSchema }),
  ev('installment.overdue', 'L37', ['relances P1', 'tableau de bord'], 'Échéance en retard.', {
    ...PERSONNE,
    installmentId: IdSchema,
    dueAt: LocalDateSchema,
  }),
  ev('billing.ruleset_versioned', 'L37', ['journal'], 'Nouvelle version du jeu de règles de facturation.', {
    dossierId,
    versionNo: z.number().int().min(1),
  }),
  ev('invoice.issued', 'L37', ['journal'], 'Facture émise (immuable).', { dossierId, invoiceId: IdSchema, number: z.string() }),
  ev('deadline.approaching', 'L33', ['tâches', 'décisions'], 'Échéance légale proche.', {
    dossierId,
    deadlineId: IdSchema,
    dueAt: LocalDateSchema,
  }),
  ev('beta.answered', 'L39', ['réglages appris', 'export Git'], 'Question BETA répondue.', {
    questionKey: z.string(),
    answeredBy: IdSchema,
    dossierId: IdSchema.nullable(),
  }),
  ev('impersonation.started', 'L30', ['journal', 'alerte admin'], 'Début d’une session « voir en tant que ».', {
    ...IMPERSONATION,
    mode: z.enum(['read', 'act']),
  }),
  ev('impersonation.ended', 'L30', ['journal'], 'Fin d’une session « voir en tant que ».', {
    ...IMPERSONATION,
    endedReason: z.string(),
  }),
  ev('impersonation.acted', 'L30', ['journal', 'alerte admin'], 'Action confirmée en tant que.', { ...IMPERSONATION, action: z.string() }),
] as const;

export type Evenement = (typeof EVENEMENTS)[number];
export type TypeEvenement = Evenement['type'];

export function evenement<T extends TypeEvenement>(type: T): Extract<Evenement, { type: T }> {
  const e = EVENEMENTS.find((x) => x.type === type);
  if (!e) throw new Error(`Événement inconnu : ${type}`);
  return e as Extract<Evenement, { type: T }>;
}

export const EnveloppeEvenementSchema = z
  .object({
    id: IdSchema,
    type: z.enum(EVENEMENTS.map((e) => e.type) as unknown as [TypeEvenement, ...TypeEvenement[]]),
    version: z.literal(1),
    aggregateType: z.string().min(1),
    aggregateId: IdSchema,
    occurredAt: InstantSchema,
    payload: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'EnveloppeEvenement', description: 'Enveloppe commune des événements de domaine.' });

/** Valide l'enveloppe puis la charge selon le type déclaré. */
export function validerEvenement(brut: unknown) {
  return EnveloppeEvenementSchema.superRefine((env, ctx) => {
    const resultat = evenement(env.type).charge.safeParse(env.payload);
    if (!resultat.success) {
      for (const issue of resultat.error.issues) ctx.addIssue({ code: 'custom', message: issue.message, path: ['payload', ...issue.path] });
    }
  }).safeParse(brut);
}
